// INTEGRITY SCREENER — Scoring Engine
// Mixed scoring: Likert 1-5 + flag item detection
// Risk score + flag count = composite risk assessment
import { INTEGRITY_ITEMS, INTEGRITY_DIMS } from "./questionBank.js";

export const IG_NORMS = {
  overall: { mean:108.4, sd:14.6, n:920 }
};

// Risk bands — primary output
export const IG_RISK_BANDS = [
  { min:0,  max:25,  level:"high_risk",     label:"High Risk",
    color:"#dc2626", bg:"#fef2f2", border:"#fecaca", icon:"🔴",
    hire:"Not recommended for trust-critical roles. Significant integrity risk indicators present.",
    desc:"Multiple integrity concern markers identified. Recommend decline or careful investigation before considering for roles with financial, data or physical asset responsibility." },
  { min:25, max:50,  level:"elevated",      label:"Elevated Risk",
    color:"#d97706", bg:"#fef3c7", border:"#fde68a", icon:"🟠",
    hire:"Exercise caution. Structured interview and reference checks required before proceeding.",
    desc:"Some integrity risk markers present. Not an automatic disqualification but warrants investigation. Suitable for lower-risk roles with supervision." },
  { min:50, max:75,  level:"moderate",      label:"Moderate — Acceptable",
    color:"#0891b2", bg:"#ecfeff", border:"#a5f3fc", icon:"🔵",
    hire:"Acceptable for most roles. Standard onboarding and monitoring recommended.",
    desc:"No significant integrity risk flags. Acceptable profile for the majority of professional roles." },
  { min:75, max:101, level:"low_risk",      label:"Low Risk",
    color:"#059669", bg:"#f0fdf4", border:"#86efac", icon:"🟢",
    hire:"Recommended for trust-critical roles. Strong integrity profile.",
    desc:"Strong honesty, compliance and reliability profile. Well-suited for roles with financial authority, asset custody or confidential information access." },
];

// Dimension-level risk thresholds (score as % of max)
export const IG_DIM_THRESHOLDS = {
  HH: { low:50, moderate:65, high:80 }, // Honesty — strict
  OI: { low:55, moderate:70, high:82 }, // Overt integrity — strictest
  RC: { low:50, moderate:65, high:78 },
  CF: { low:50, moderate:65, high:80 }, // Compliance — strict for financial roles
  CW: { low:50, moderate:65, high:80 },
};

export function scoreIntegrity(answers) {
  const dimScores  = { HH:0, OI:0, RC:0, CF:0, CW:0 };
  const dimCounts  = { HH:0, OI:0, RC:0, CF:0, CW:0 };
  const flagsRaised = [];

  INTEGRITY_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    const val = item.reverse ? (6 - raw) : raw;
    dimScores[item.dim] += val;
    dimCounts[item.dim]++;

    // Flag detection — check if response crosses the flag threshold
    if (item.flag) {
      const rawScore = Number(raw);
      const shouldFlag = item.reverse
        ? rawScore >= (item.flagAt || 3)   // high score on reverse = bad
        : rawScore <= (item.flagAt || 3);  // low score on normal = bad
      if (shouldFlag) {
        flagsRaised.push({
          item,
          response: rawScore,
          responseLabel: ["","Strongly Disagree","Disagree","Neutral","Agree","Strongly Agree"][rawScore]||rawScore,
          note: item.flagNote,
          severity: item.reverse
            ? (rawScore >= 5 ? "critical" : rawScore >= 4 ? "high" : "moderate")
            : (rawScore <= 1 ? "critical" : rawScore <= 2 ? "high" : "moderate"),
        });
      }
    }
  });

  const dimResults = {};
  let total = 0, maxTotal = 0;

  Object.keys(INTEGRITY_DIMS).forEach(dim => {
    const raw   = dimScores[dim];
    const count = dimCounts[dim];
    const max   = count * 5;
    const pct   = max > 0 ? Math.round((raw / max) * 100) : 0;
    const t     = IG_DIM_THRESHOLDS[dim];
    const level = pct >= t.high ? "high" : pct >= t.moderate ? "moderate" : "low";
    dimResults[dim] = { raw, max, pct, level, count };
    total    += raw;
    maxTotal += max;
  });

  const totalPct   = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
  const critFlags  = flagsRaised.filter(f => f.severity === "critical").length;
  const highFlags  = flagsRaised.filter(f => f.severity === "high").length;

  // Adjust risk score down for flags (each critical flag = -10 points, high = -5)
  const adjustedPct = Math.max(0, totalPct - critFlags * 10 - highFlags * 5);
  const band        = IG_RISK_BANDS.find(b => adjustedPct >= b.min && adjustedPct < b.max) || IG_RISK_BANDS[0];

  const norm = IG_NORMS.overall;
  const z = (total - norm.mean) / norm.sd;
  const percentile = Math.min(99, Math.max(1, Math.round(50 + z * 17)));

  return {
    dimResults,
    total, maxTotal, totalPct, adjustedPct,
    percentile, band,
    flagsRaised,
    critFlags, highFlags,
    riskScore: 100 - adjustedPct, // Invert so high = more risky (intuitive for employers)
    scales: { integrity: percentile },
    perfAvg: totalPct/20, potAvg: totalPct/20,
    perfBox: adjustedPct>=75?3:adjustedPct>=50?2:1,
    potBox:  adjustedPct>=75?3:adjustedPct>=50?2:1,
  };
}
